import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Typography,
  Chip,
  Button,
  Spinner,
  IconButton,
  Tooltip,
} from '@material-tailwind/react';
import {
  CurrencyDollarIcon,
  ShieldCheckIcon,
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/outline';

const formatPrice = (value, currency = 'USD') => {
  if (!value && value !== 0) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(value);
};

const getConfidenceColor = (confidence) => {
  if (!confidence) return 'gray';
  const lower = confidence.toLowerCase();
  if (lower.includes('high')) return 'green';
  if (lower.includes('medium')) return 'amber';
  if (lower.includes('low')) return 'red';
  return 'gray';
};

const CONFIDENCE_OPTIONS = [
  'High',
  'Medium-High',
  'Medium',
  'Medium-Low',
  'Low',
];

// Helper to extract values from content
const getContentValues = (content) => {
  if (!content) {
    return {
      estimatedValue: '',
      lowValue: '',
      highValue: '',
      confidence: '',
      appraisalText: '',
      methodology: '',
    };
  }
  return {
    estimatedValue: (content.estimatedValue || '').toString(),
    lowValue: (content.expectedRange?.low || '').toString(),
    highValue: (content.expectedRange?.high || '').toString(),
    confidence: content.confidenceLevel || '',
    appraisalText: content.appraisalText || '',
    methodology: content.methodology || '',
  };
};

function SummaryPricingSection({ content, onSave, onReset, isSaving }) {
  // Create a stable key from content to detect actual changes
  const contentKey = useMemo(() => {
    const vals = getContentValues(content);
    return JSON.stringify(vals);
  }, [content]);

  // Editable state
  const [editedEstimatedValue, setEditedEstimatedValue] = useState('');
  const [editedLowValue, setEditedLowValue] = useState('');
  const [editedHighValue, setEditedHighValue] = useState('');
  const [editedConfidence, setEditedConfidence] = useState('');
  const [editedAppraisalText, setEditedAppraisalText] = useState('');
  const [editedMethodology, setEditedMethodology] = useState('');
  const [hideAppraisalText, setHideAppraisalText] = useState(false);
  const [hideMethodology, setHideMethodology] = useState(false);
  const [hideDisclaimer, setHideDisclaimer] = useState(false);

  // Reset form when content actually changes (using contentKey)
  useEffect(() => {
    const vals = getContentValues(content);
    setEditedEstimatedValue(vals.estimatedValue);
    setEditedLowValue(vals.lowValue);
    setEditedHighValue(vals.highValue);
    setEditedConfidence(vals.confidence);
    setEditedAppraisalText(vals.appraisalText);
    setEditedMethodology(vals.methodology);
    setHideAppraisalText(content?.hideAppraisalText || false);
    setHideMethodology(content?.hideMethodology || false);
    setHideDisclaimer(content?.hideDisclaimer || false);
  }, [contentKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Compute hasChanges directly (no useEffect needed)
  const hasChanges = useMemo(() => {
    const orig = getContentValues(content);
    return (
      editedEstimatedValue !== orig.estimatedValue ||
      editedLowValue !== orig.lowValue ||
      editedHighValue !== orig.highValue ||
      editedConfidence !== orig.confidence ||
      editedAppraisalText !== orig.appraisalText ||
      editedMethodology !== orig.methodology ||
      hideAppraisalText !== (content?.hideAppraisalText || false) ||
      hideMethodology !== (content?.hideMethodology || false) ||
      hideDisclaimer !== (content?.hideDisclaimer || false)
    );
  }, [
    editedEstimatedValue,
    editedLowValue,
    editedHighValue,
    editedConfidence,
    editedAppraisalText,
    editedMethodology,
    hideAppraisalText,
    hideMethodology,
    hideDisclaimer,
    content,
  ]);

  const handleSave = useCallback(() => {
    if (onSave) {
      onSave({
        estimatedValue: editedEstimatedValue ? parseInt(editedEstimatedValue, 10) : undefined,
        expectedRange: {
          low: editedLowValue ? parseInt(editedLowValue, 10) : undefined,
          high: editedHighValue ? parseInt(editedHighValue, 10) : undefined,
        },
        confidenceLevel: editedConfidence || undefined,
        appraisalText: editedAppraisalText || undefined,
        methodology: editedMethodology || undefined,
        hideAppraisalText,
        hideMethodology,
        hideDisclaimer,
      });
    }
  }, [onSave, editedEstimatedValue, editedLowValue, editedHighValue, editedConfidence, editedAppraisalText, editedMethodology, hideAppraisalText, hideMethodology, hideDisclaimer]);

  const handleReset = useCallback(() => {
    const vals = getContentValues(content);
    setEditedEstimatedValue(vals.estimatedValue);
    setEditedLowValue(vals.lowValue);
    setEditedHighValue(vals.highValue);
    setEditedConfidence(vals.confidence);
    setEditedAppraisalText(vals.appraisalText);
    setEditedMethodology(vals.methodology);
    setHideAppraisalText(content?.hideAppraisalText || false);
    setHideMethodology(content?.hideMethodology || false);
    setHideDisclaimer(content?.hideDisclaimer || false);
    if (onReset) {
      onReset();
    }
  }, [content, onReset]);

  if (!content) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500">
        <Typography>No pricing data available</Typography>
      </div>
    );
  }

  const currency = content.currency || 'USD';
  const dataPoints = content.dataPoints || content.data_points;
  const confidenceColor = getConfidenceColor(editedConfidence);

  return (
    <div className="space-y-4 p-3 overflow-auto h-full">
      {/* Hero Pricing Card - Display */}
      <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 text-center">
        <div className="flex items-center justify-center gap-2 mb-2">
          <CurrencyDollarIcon className="h-5 w-5 text-blue-600" />
          <Typography variant="small" className="uppercase tracking-wide text-blue-600 font-semibold text-xs">
            Estimated Value
          </Typography>
          {hasChanges && (
            <Chip size="sm" value="Modified" color="amber" variant="ghost" className="ml-2" />
          )}
        </div>

        <Typography variant="h3" color="blue" className="mb-2">
          {formatPrice(editedEstimatedValue ? parseInt(editedEstimatedValue, 10) : null, currency)}
        </Typography>

        {(editedLowValue || editedHighValue) && (
          <Typography variant="small" color="gray" className="mb-2">
            Range: {formatPrice(editedLowValue ? parseInt(editedLowValue, 10) : null, currency)} — {formatPrice(editedHighValue ? parseInt(editedHighValue, 10) : null, currency)}
          </Typography>
        )}

        {editedConfidence && (
          <div className="flex justify-center">
            <Chip
              size="sm"
              value={editedConfidence}
              color={confidenceColor}
              icon={<ShieldCheckIcon className="h-4 w-4" />}
            />
          </div>
        )}

        {dataPoints !== undefined && (
          <Typography variant="small" color="gray" className="mt-2 text-xs">
            Based on {dataPoints} comparable data points
          </Typography>
        )}
      </div>

      {/* Editable Pricing Fields */}
      <div className="space-y-3">
        <Typography variant="small" className="font-semibold text-sm text-gray-800">
          Pricing
        </Typography>

        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Low Value</label>
            <input
              type="number"
              value={editedLowValue}
              onChange={(e) => setEditedLowValue(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Estimated Value</label>
            <input
              type="number"
              value={editedEstimatedValue}
              onChange={(e) => setEditedEstimatedValue(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">High Value</label>
            <input
              type="number"
              value={editedHighValue}
              onChange={(e) => setEditedHighValue(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Confidence Level</label>
          <select
            value={editedConfidence}
            onChange={(e) => setEditedConfidence(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 bg-white"
          >
            <option value="">Select...</option>
            {CONFIDENCE_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
      </div>

      <hr className="border-gray-200" />

      {/* Appraisal Text - Editable */}
      <div className={hideAppraisalText ? 'opacity-50' : ''}>
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <Typography variant="small" className="font-semibold text-sm text-gray-800">
              Valuation Summary
            </Typography>
            {hideAppraisalText && (
              <span className="text-xs text-gray-400 italic">Hidden</span>
            )}
          </div>
          <Tooltip content={hideAppraisalText ? 'Show in report' : 'Hide from report'}>
            <IconButton
              variant="text"
              size="sm"
              onClick={() => setHideAppraisalText(!hideAppraisalText)}
            >
              {hideAppraisalText ? (
                <EyeSlashIcon className="h-4 w-4 text-gray-400" />
              ) : (
                <EyeIcon className="h-4 w-4" />
              )}
            </IconButton>
          </Tooltip>
        </div>
        <textarea
          value={editedAppraisalText}
          onChange={(e) => setEditedAppraisalText(e.target.value)}
          rows={4}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 resize-y"
        />
      </div>

      <hr className="border-gray-200" />

      {/* Methodology - Editable */}
      <div className={hideMethodology ? 'opacity-50' : ''}>
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <Typography variant="small" className="font-semibold text-sm text-gray-800">
              Methodology
            </Typography>
            {hideMethodology && (
              <span className="text-xs text-gray-400 italic">Hidden</span>
            )}
          </div>
          <Tooltip content={hideMethodology ? 'Show in report' : 'Hide from report'}>
            <IconButton
              variant="text"
              size="sm"
              onClick={() => setHideMethodology(!hideMethodology)}
            >
              {hideMethodology ? (
                <EyeSlashIcon className="h-4 w-4 text-gray-400" />
              ) : (
                <EyeIcon className="h-4 w-4" />
              )}
            </IconButton>
          </Tooltip>
        </div>
        <textarea
          value={editedMethodology}
          onChange={(e) => setEditedMethodology(e.target.value)}
          rows={3}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 resize-y"
        />
      </div>

      <hr className="border-gray-200" />

      {/* Disclaimer */}
      <div className={hideDisclaimer ? 'opacity-50' : ''}>
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <Typography variant="small" className="font-semibold text-sm text-gray-800">
              Disclaimer
            </Typography>
            {hideDisclaimer && (
              <span className="text-xs text-gray-400 italic">Hidden</span>
            )}
          </div>
          <Tooltip content={hideDisclaimer ? 'Show in report' : 'Hide from report'}>
            <IconButton
              variant="text"
              size="sm"
              onClick={() => setHideDisclaimer(!hideDisclaimer)}
            >
              {hideDisclaimer ? (
                <EyeSlashIcon className="h-4 w-4 text-gray-400" />
              ) : (
                <EyeIcon className="h-4 w-4" />
              )}
            </IconButton>
          </Tooltip>
        </div>
        <p className="text-xs text-gray-500 italic">
          "This valuation is based on market analysis..."
        </p>
      </div>

      {/* Save/Reset Buttons */}
      {onSave && (
        <div className="flex justify-end gap-2 pt-2">
          <Button
            variant="outlined"
            size="sm"
            onClick={handleReset}
            disabled={!hasChanges || isSaving}
            className="py-1 px-3 text-xs"
          >
            Reset
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
            className="py-1 px-3 text-xs"
          >
            {isSaving ? (
              <>
                <Spinner className="h-3 w-3 mr-1" />
                Saving...
              </>
            ) : (
              'Save'
            )}
          </Button>
        </div>
      )}
    </div>
  );
}

export default SummaryPricingSection;
